// Single source of truth for "does this draft have everything it needs?".
// Consumed by the gift-flow stepper (GiftContext) to gate step access, and by
// the /my-gifts detail page to decide between "Continue Editing" and checkout.
// Both screens must agree, so keep the rules here rather than in either caller.

export interface GiftCompletionInput {
  recipientId?: string | null;
  type?: string | null;
  occasionType?: string | null;
  occasion?: string | null;
  // Date in the stepper, ISO string off the API — only ever checked for presence.
  date?: unknown;
  milestone?: string | null;
  milestoneEstimateDate?: unknown;
  deliverySchedule?: string | null;
  numberOfYears?: string | number | null;
  messages?: Array<{ text?: string | null; audioUrl?: string | null }> | null;
  giftSection?: {
    monetary?: unknown;
    giftCard?: unknown;
    physicalItem?: unknown;
  } | null;
}

export const hasGiftBasics = (gift?: GiftCompletionInput | null): boolean => {
  if (!gift?.recipientId || !gift?.type) {
    return false;
  }

  if (gift.occasionType === 'Conditional') {
    return !!(gift.milestone && gift.milestoneEstimateDate);
  }

  // 'Fixed', plus the legacy case where occasionType was never persisted.
  return !!(gift.occasion && gift.date);
};

export const hasGiftMessages = (gift?: GiftCompletionInput | null): boolean => {
  if (!gift?.messages || gift.messages.length === 0) {
    return false;
  }

  // Recurring gifts carry one message per year regardless of frequency:
  // annual/monthly/weekly all repeat a single message within each year.
  let expectedMessageCount = 1;
  if (gift.deliverySchedule === 'Recurring') {
    expectedMessageCount = parseInt(String(gift.numberOfYears ?? ''), 10) || 1;
  }

  if (gift.messages.length !== expectedMessageCount) {
    return false;
  }

  return gift.messages.every((msg) => !!(msg?.text?.trim() && msg?.audioUrl));
};

export const hasGiftSelection = (gift?: GiftCompletionInput | null): boolean =>
  !!(
    gift?.giftSection &&
    (gift.giftSection.monetary ||
      gift.giftSection.giftCard ||
      gift.giftSection.physicalItem)
  );

// True when nothing is left to fill in and the only thing standing between the
// draft and a scheduled gift is payment.
export const isGiftReadyForPayment = (gift?: GiftCompletionInput | null): boolean =>
  hasGiftBasics(gift) && hasGiftMessages(gift) && hasGiftSelection(gift);
